import { useQuery } from '@apollo/client';
import React, { FC } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';

import Breadcrumbs from 'Components/Breadcrumbs/Breadcrumbs';
import { useCurrentUser } from 'Components/CurrentUser/CurrentUser';
import PageScroller from 'Components/PageScroller/PageScroller';
import BreadcrumbList from 'Components/SeoSchema/BreadcrumbList';
import PageView from 'common/PageView/PageView';
import { isWindow } from 'common/helpers/helpers';
import config from 'config/config';
import categoryIcon from 'uikit/Icons/Categories';
import routes from 'utils/routeTranslator';

import Benefits from '../Benefits';
import ProviderBlog from '../Blog/ProviderBlog';
import Faq from '../Faq';
import GetOrder from '../GetOrder';
import RegisterHero from '../Hero/RegisterHero';
import LatestRequests from '../LatestRequests';
import RegisterOrdersByCities from '../OrdersByCities/OrdersByCities';
import {
  defaultPageDescription,
  defaultPageTitle,
  getWeekRequestsText,
  mainPageBreadcrumb,
  mainPageBreadcrumbSeo,
  redirectAuthenticatedUser,
} from '../ProviderRegister.helpers';
import RegisterMeta from '../RegisterMeta/RegisterMeta';
import RegisterRelatedOrders from '../RelatedOrders/RegisterRelatedOrders';
import { providerRegisterL2Query, ProviderRegisterL2Response } from './ProviderRegisterL2.helpers';
import './ProviderRegisterL2.scss';

const ProviderRegisterL2: FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const match = useRouteMatch(routes.get('front::provider.register.l2.requests'));
  const { currentUser } = useCurrentUser();
  const { data } = useQuery<ProviderRegisterL2Response>(providerRegisterL2Query, {
    variables: {
      slug,
    },
    skip: Boolean(currentUser?.userId),
  });

  // redirect authenticated user to dashboard
  if (isWindow && currentUser && currentUser.userId) {
    return redirectAuthenticatedUser(Boolean(currentUser.isProvider));
  }

  if (!data) return null;

  const interlinkingEnabled = !match;

  const l2breadcrumbName = (
    <span className="providerRegisterL2__segment">
      {categoryIcon(data.l2BySlug.slug)} {data.l2BySlug.name}
    </span>
  );

  const breadcrumbsPath = [
    mainPageBreadcrumb,
    {
      name: l2breadcrumbName,
      url: '',
    },
  ];

  const breadcrumbsSeo = [
    mainPageBreadcrumbSeo,
    {
      name: data.l2BySlug.name,
      url: '',
    },
  ];

  const { metaDescription, metaTitle, coverUrl, mobileCoverUrl } = data.l2BySlug.spRegistration;

  return (
    <main>
      <RegisterMeta
        title={metaTitle || defaultPageTitle}
        description={metaDescription || defaultPageDescription}
        canonicalUrl={config.SITE_URL + routes.get('front::provider.register.l2', { slug })}
        ogPreviewUrl={coverUrl}
        thumbnail={mobileCoverUrl}
      />
      <PageView pageLabel="provider_registration_start" touch-point-page={slug} />
      <RegisterHero
        l2CategoryId={data.l2BySlug.id}
        prefixContent={<Breadcrumbs path={breadcrumbsPath} separator="»" />}
        suffixContent={getWeekRequestsText(data.l2BySlug.requestCountSinceLastWeek)}
      />
      <BreadcrumbList items={breadcrumbsSeo} />
      <LatestRequests l2Slug={slug} />
      <GetOrder className="providerRegisterL2__ctaSection" />
      <Benefits />
      {interlinkingEnabled && <Faq />}
      {interlinkingEnabled && <RegisterOrdersByCities />}
      {interlinkingEnabled && <RegisterRelatedOrders services={data.l2BySlug.categories} />}
      <ProviderBlog />
      <PageScroller />
    </main>
  );
};

export default ProviderRegisterL2;
